import { JOGOS_360, LAGGED_ADDITIONAL_CLID, LAGGED_CLID, PLAY_GAMES_CLID } from '@/utils/clid-config'
import { timer } from '@/utils/helpers'

import { OutOfPageAdType } from '../types'
import {
    createProgressAnimation,
    MAX_PROGRESS,
    PROGRESS_FINISH_DURATION,
    ProgressAnimationController,
} from '../utils/progress-animation'
import { scopedClassNames } from '../utils/scoped-class-names'
import jogos360Logo from './images/jogos-360-logo.webp'
import laggedLogo from './images/lagged-logo.webp'
import playGamesLogo from './images/play-games.webp'
import pockethavenLogo from './images/pockethaven.webp'
import { buildPlaygamaAffiliateProgressLogoSvg, LOGO_CLIP_AFFILIATE_ID } from './templates/playgama-affiliate-logo'
import { buildPlaygamaLogoSvg, PORTAL_LOGO_CLIP_ID } from './templates/playgama-logo'

const LOGO_IMAGES: Record<string, string> = {
    pockethaven: pockethavenLogo,
    'play-games': playGamesLogo,
    [PLAY_GAMES_CLID]: playGamesLogo,
    [LAGGED_CLID]: laggedLogo,
    [LAGGED_ADDITIONAL_CLID]: laggedLogo,
    [JOGOS_360]: jogos360Logo,
}

export type LoaderConfig = {
    logoId?: string
    progressColor?: string
    affiliate?: boolean
    adTypes?: OutOfPageAdType[]
    getTimeout?: (adType: OutOfPageAdType) => number
    subtitlesByAdType?: Partial<Record<OutOfPageAdType, string>>
}

export class AdLoader {
    private readonly host: HTMLDivElement
    private readonly progressBar: HTMLDivElement
    private readonly clipRect: SVGRectElement | null
    private readonly logoSrc: string | undefined
    private readonly affiliate: boolean
    private readonly subtitleEl: HTMLDivElement
    private readonly adTypes: OutOfPageAdType[]
    private readonly getTimeout: ((adType: OutOfPageAdType) => number) | undefined
    private readonly subtitlesByAdType: Partial<Record<OutOfPageAdType, string>>

    private durationMs: number | undefined

    private hidden = false
    private animation: ProgressAnimationController | undefined

    constructor(config: LoaderConfig) {
        this.affiliate = !!config.affiliate
        this.adTypes = config.adTypes || []
        this.getTimeout = config.getTimeout
        this.subtitlesByAdType = config.subtitlesByAdType || {}
        this.logoSrc = this.affiliate && config.logoId ? LOGO_IMAGES[config?.logoId] : undefined
        const { progressColor = '#aa76ff' } = config

        this.host = document.createElement('div')
        const shadow = this.host.attachShadow({ mode: 'closed' })

        const cls = scopedClassNames(['overlay', 'logo', 'logoImg', 'progressTrack', 'progressBar', 'subtitle'])
        const style = document.createElement('style')
        style.textContent = `
            :host {
                position: fixed;
                inset: 0;
                z-index: 2147483647;
                display: none;
            }

            .${cls.overlay} {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background: rgba(0, 0, 0, 0.9);
                padding: 20px;
                box-sizing: border-box;
            }

            .${cls.logo} {
                margin-bottom: clamp(16px, 3vw, 32px);
                width: clamp(200px, 30vw, 400px);
                display: flex;
                justify-content: center;
            }

            .${cls.logo} svg {
                width: 100%;
                height: auto;
            }

            .${cls.logoImg} {
                width: 100%;
                height: auto;
                max-height: clamp(60px, 10vw, 120px);
                object-fit: contain;
            }

            .${cls.progressTrack} {
                width: clamp(200px, 30vw, 400px);
                height: clamp(3px, 0.5vw, 6px);
                background: rgba(255, 255, 255, 0.5);
                border-radius: 3px;
                overflow: hidden;
            }

            .${cls.progressBar} {
                height: 100%;
                width: 0%;
                background: ${progressColor};
                border-radius: 3px;
                transition: none;
            }

            .${cls.subtitle} {
                margin-top: clamp(8px, 1.4vw, 16px);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: clamp(12px, 1.8vw, 16px);
                color: rgba(255, 255, 255, 0.5);
                text-align: center;
                display: none;
            }
        `

        const overlay = document.createElement('div')
        overlay.className = cls.overlay

        const logoContainer = document.createElement('div')
        logoContainer.className = cls.logo
        logoContainer.innerHTML = this.buildLogoHtml(progressColor, cls.logoImg)

        const progressTrack = document.createElement('div')
        progressTrack.className = cls.progressTrack

        this.progressBar = document.createElement('div')
        this.progressBar.className = cls.progressBar

        progressTrack.appendChild(this.progressBar)
        overlay.appendChild(logoContainer)

        if (this.logoSrc) {
            overlay.appendChild(progressTrack)
        }

        this.subtitleEl = document.createElement('div')
        this.subtitleEl.className = cls.subtitle
        overlay.appendChild(this.subtitleEl)

        shadow.appendChild(style)
        shadow.appendChild(overlay)

        document.body.appendChild(this.host)

        if (!this.affiliate) {
            this.clipRect = shadow.querySelector(`#${PORTAL_LOGO_CLIP_ID} rect`) as SVGRectElement | null
        } else if (!this.logoSrc) {
            this.clipRect = shadow.querySelector(`#${LOGO_CLIP_AFFILIATE_ID} rect`) as SVGRectElement | null
        } else {
            this.clipRect = null
        }
    }

    private buildLogoHtml(progressColor: string, logoImgClass: string): string {
        if (!this.affiliate) {
            return buildPlaygamaLogoSvg(progressColor)
        }

        if (!this.logoSrc) {
            return buildPlaygamaAffiliateProgressLogoSvg(progressColor)
        }

        return `<img src="${this.logoSrc}" alt="affiliate-progress-logo" class="${logoImgClass}" />`
    }

    private updateProgress(value: number): void {
        const percent = Math.min(value, MAX_PROGRESS)
        if (this.logoSrc) {
            this.progressBar.style.width = `${percent}%`
        } else if (this.clipRect) {
            this.clipRect.setAttribute('width', `${percent}%`)
        }
    }

    private setAnimation(timeout: number): void {
        if (this.durationMs !== timeout) {
            this.durationMs = timeout
            if (this.animation) {
                this.animation.cancel()
                this.animation = undefined
            }
        }
        if (!this.animation) {
            this.animation = createProgressAnimation({
                slowDuration: this.durationMs,
                onProgress: (value: number) => this.updateProgress(value),
                shouldStop: () => this.hidden,
            })
        }
    }

    private toggleVisibility(visible: boolean, adType?: OutOfPageAdType): void {
        this.hidden = !visible
        this.host.style.display = visible ? 'block' : 'none'

        let subtitle = adType && this.subtitlesByAdType[adType]
        // Only show the attribution sign when a real partner logo/custom banner is being
        // displayed. When affiliate is true but there's no custom logoSrc, we're still showing
        // our own generic Playgama-branded loader, so the sign would be misleading there.
        if (this.affiliate && this.logoSrc) {
            subtitle = 'Ads powered by Playgama'
        }

        this.subtitleEl.textContent = subtitle ?? null
        this.subtitleEl.style.display = subtitle ? 'block' : 'none'
    }

    show(adType: OutOfPageAdType): void {
        if (this.adTypes.length && !this.adTypes.includes(adType)) {
            return
        }
        this.setAnimation(this.getTimeout?.(adType) ?? 0)
        this.toggleVisibility(true, adType)
        this.animation?.start()
    }

    async destroy(): Promise<void> {
        if (this.hidden) {
            return
        }
        this.animation?.finish()
        await timer(PROGRESS_FINISH_DURATION)
        this.toggleVisibility(false)
    }
}
